import {
	BackendService,
	IInventoryService,
	notifyOnError,
	RpcService,
	VarsService,
} from '~/rage';
import { Subject } from '~/rage/utils';
import {
	FishingCatchResult,
	HobbyFishingMinigameConfig,
	InventoryType,
	PackedInventory,
} from '~/shared';
import { GameConfigService } from '../GameConfigService';
import {
	DEFAULT_FISHING_DIFFICULTY,
	getFishingDifficultyByRarity,
} from './fishingDifficulty';
import {
	HobbyFishingFishClientConfig,
	IBait,
	IGameFishingService,
	IHobbyFishingDifficulty,
	IHobbyFishingHookedFish,
} from './gameFishingServiceTypes';

const BAIT_ITEM_IDS = [
	'dough',
	'bread',
	'worm',
	'corn',
	'maggots',
	'flies',
	'shrimps',
	'boilies',
];

function getBaseItemId(id: string): string {
	if (!id) return '';
	if (id.includes('-equip-')) return id.split('-equip-')[0];
	if (id.includes('-split-')) return id.split('-split-')[0];
	return id;
}

function countBaitsInInventory(
	inventory: PackedInventory | null,
): Map<string, number> {
	const counts = new Map<string, number>();
	if (!inventory?.items) return counts;

	for (const stack of inventory.items) {
		if (!stack) continue;
		const baseId = getBaseItemId(stack.id);
		if (BAIT_ITEM_IDS.includes(baseId)) {
			const prev = counts.get(baseId) ?? 0;
			counts.set(baseId, prev + stack.count);
		}
	}
	return counts;
}

export class GameFishingService implements IGameFishingService {
	private locationId = new Subject<string>('');
	private avaliableBaits = new Subject<IBait[]>([]);
	private backpackAvaliable = new Subject<boolean>(false);
	private hookedFish = new Subject<IHobbyFishingHookedFish | null>(null);
	private difficulty = new Subject<IHobbyFishingDifficulty>(
		DEFAULT_FISHING_DIFFICULTY,
	);

	private fish = new Subject<HobbyFishingFishClientConfig[]>([]);
	private playerInventory: PackedInventory | null = null;
	private backpackInventory: PackedInventory | null = null;
	private hotbarInventory: PackedInventory | null = null;

	private minigameConfig = new Subject<HobbyFishingMinigameConfig>({
		fishEscapeChance: 0,
		waitGreenSecMin: 0,
		waitGreenSecMax: 0,
		waitOrangeSecMin: 0,
		waitOrangeSecMax: 0,
		waitOtherSecMin: 0,
		waitOtherSecMax: 0,
	});

	constructor(
		private readonly gameConfig: GameConfigService,
		private readonly rpc: RpcService,
		private readonly vars: VarsService,
		private readonly backend: BackendService,
		private readonly inventoryService: IInventoryService,
	) {
		this.vars.get<string>('gameFishing:locationId', '').subscribe((value) => {
			this.locationId.next(value);
		});

		this.gameConfig.getConfigs().subscribe((config) => {
			if (!config || !config.hobbyFishing) {
				return;
			}

			this.fish.next(config.hobbyFishing.fishes);
			this.minigameConfig.next(config.hobbyFishing.minigame);
		});

		this.backend.addEventHandler(
			'hobbyFishing:onHook',
			(data: { catchResult: FishingCatchResult }) => {
				this.setHookedFish(data.catchResult);
			},
		);

		this.backend.addEventHandler(
			'hobbyFishing:onCatch',
			(data: { catchResult: FishingCatchResult; isFullInventory: boolean }) => {
				if (this.hookedFish.getValue()) {
					return;
				}

				this.setHookedFish(data.catchResult);
			},
		);

		this.inventoryService
			.getInventoryByType(InventoryType.PLAYER)
			.subscribe((inventory) => {
				this.playerInventory = inventory;
				this.updateAvailableBaits();
			});

		this.inventoryService
			.getInventoryByType(InventoryType.BACKPACK)
			.subscribe((inventory) => {
				this.backpackInventory = inventory;
				this.backpackAvaliable.next(!!inventory);
				this.updateAvailableBaits();
			});

		this.inventoryService
			.getInventoryByType(InventoryType.HOTBAR)
			.subscribe((inventory) => {
				this.hotbarInventory = inventory;
				this.updateAvailableBaits();
			});
	}

	private setHookedFish(catchResult: FishingCatchResult): void {
		const fishConfig = this.fish
			.getValue()
			.find(
				(e) =>
					e.fishId === catchResult.fishId && e.rarity === catchResult.rarity,
			);

		if (!fishConfig) {
			return;
		}

		const itemName = this.inventoryService.getItemNameById(fishConfig.fishId);

		this.hookedFish.next({
			...fishConfig,
			name: itemName ?? '???',
			weight: catchResult.weight,
		});
		this.difficulty.next(getFishingDifficultyByRarity(fishConfig.rarity));
	}

	private resetHookedFish(): void {
		this.hookedFish.next(null);
		this.difficulty.next(DEFAULT_FISHING_DIFFICULTY);
	}

	private updateAvailableBaits(): void {
		const totalCounts = new Map<string, number>();

		for (const inv of [
			this.playerInventory,
			this.backpackInventory,
			this.hotbarInventory,
		]) {
			const counts = countBaitsInInventory(inv);
			counts.forEach((count, itemId) => {
				const prev = totalCounts.get(itemId) ?? 0;
				totalCounts.set(itemId, prev + count);
			});
		}

		const baits: IBait[] = BAIT_ITEM_IDS.map((itemId) => ({
			itemId,
			count: totalCounts.get(itemId) ?? 0,
		})).filter((b) => b.count > 0);

		this.avaliableBaits.next(baits);
	}

	@notifyOnError
	public async reportWin(isBackpackEnabled: boolean): Promise<void> {
		await this.rpc.invokeClient('hobbyFishing:reportWin', {
			isBackpackEnabled,
		});
	}

	public getHookedFish(): Subject<IHobbyFishingHookedFish | null> {
		return this.hookedFish;
	}

	public getDifficulty(): Subject<IHobbyFishingDifficulty> {
		return this.difficulty;
	}

	@notifyOnError
	public async reportLoss(): Promise<void> {
		this.resetHookedFish();
		await this.rpc.invokeClient('hobbyFishing:reportLoss');
	}

	@notifyOnError
	public async start(baitItemId?: string): Promise<void> {
		this.resetHookedFish();
		await this.rpc.invokeClient('hobbyFishing:start', { baitItemId });
	}

	@notifyOnError
	public async stop(): Promise<void> {
		this.resetHookedFish();
		await this.rpc.invokeClient('hobbyFishing:stop');
	}

	@notifyOnError
	public async releaseFish(fishItemId: string): Promise<void> {
		await this.rpc.invokeServer('hobbyFishing:releaseFish', {
			locationId: this.locationId.getValue(),
			fishItemId,
		});
	}

	public getAvaliableBaits(): Subject<IBait[]> {
		return this.avaliableBaits;
	}

	public getBackpackAvaliable(): Subject<boolean> {
		return this.backpackAvaliable;
	}

	public getMinigameConfig(): Subject<HobbyFishingMinigameConfig> {
		return this.minigameConfig;
	}

	@notifyOnError
	public async exit(): Promise<void> {
		this.resetHookedFish();
		await this.rpc.invokeClient('hobbyFishing:closeMenu');
	}
}
